import React from 'react';
import {
  Brain, Sparkles, TrendingUp, Flame, Snowflake,
  Target, Clock, BarChart3,
} from 'lucide-react';
import ShareButtons from '../ShareButtons';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const STRATEGY_STYLES = {
  conservadora: 'bg-blue-500/20 text-blue-300',
  balanceada: 'bg-green-500/20 text-green-300',
  arriesgada: 'bg-orange-500/20 text-orange-300',
};

export const PredictionResultCard = ({ prediction }) => (
  <Card className="overflow-hidden border-0 shadow-2xl" data-testid="prediction-main-card">
    <div
      className="p-1"
      style={{ background: `linear-gradient(135deg, ${prediction.lottery.color}, #8b5cf6, #ec4899)` }}
    >
      <div className="bg-slate-950 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: prediction.lottery.color }}>
              <Target size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{prediction.lottery.name}</h2>
              <p className="text-slate-400 text-sm">{prediction.lottery.company}</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/50">
            <Brain size={14} className="mr-1" />{prediction.ai_powered ? 'Análisis con IA' : 'Análisis estadístico'}
          </Badge>
        </div>

        <div className="text-center mb-6">
          <p className="text-slate-400 text-sm mb-3">NUMEROS RECOMENDADOS</p>
          <div className="flex justify-center gap-4">
            {prediction.prediction.numbers.map((num, idx) => (
              <div key={`pred-num-${num}`} className="relative group">
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-white font-bold text-3xl sm:text-4xl shadow-lg transform transition-transform group-hover:scale-110"
                  style={{ background: `linear-gradient(135deg, ${prediction.lottery.color}, #8b5cf6)`, boxShadow: `0 0 30px ${prediction.lottery.color}60` }}
                >
                  {String(num).padStart(2, '0')}
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-xs font-bold text-white">{idx + 1}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400">Muestra consultada</span>
            <span className="text-lg font-bold text-green-400">{prediction.metrics.draws_analyzed} sorteos</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">Último resultado: {prediction.metrics.source_last_draw_date}. Esta selección no indica una probabilidad de ganar.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-900/50 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">ESTRATEGIA</p>
            <Badge className={STRATEGY_STYLES[prediction.prediction.strategy] || ''}>
              {prediction.prediction.strategy?.toUpperCase()}
            </Badge>
          </div>
          <div className="bg-slate-900/50 rounded-xl p-4">
            <p className="text-slate-400 text-xs mb-1">PATRON DETECTADO</p>
            <p className="text-white text-sm">{prediction.prediction.pattern_detected}</p>
          </div>
        </div>

        <div className="mt-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-4 border border-purple-500/20">
          <div className="flex items-start gap-3">
            <Sparkles size={20} className="text-purple-400 mt-1 flex-shrink-0" />
            <div>
              <p className="text-purple-300 text-sm font-semibold mb-1">Analisis de Sócrates</p>
              <p className="text-slate-300 text-sm">{prediction.prediction.reasoning}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-800">
          <ShareButtons prediction={prediction.prediction} lotteryName={prediction.lottery.name} variant="full" />
        </div>
      </div>
    </div>
  </Card>
);

export const AnalysisCards = ({ prediction }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analysis-cards">
    <Card className="glass-card border-pink-500/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Flame size={18} className="text-pink-400" />Numeros Calientes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {prediction.analysis.hot_numbers.slice(0, 8).map((item) => (
            <div key={`hot-${item.number}`} className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-300 font-mono font-bold text-sm border border-pink-500/30">
              {String(item.number).padStart(2, '0')}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <Card className="glass-card border-cyan-500/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Snowflake size={18} className="text-cyan-400" />Numeros Frios
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {prediction.analysis.cold_numbers.slice(0, 8).map((item) => (
            <div key={`cold-${item.number}`} className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 font-mono font-bold text-sm border border-cyan-500/30">
              {String(item.number).padStart(2, '0')}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    <Card className="glass-card border-amber-500/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Clock size={18} className="text-amber-400" />Numeros Rezagados
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {prediction.analysis.overdue_numbers.slice(0, 8).map((item) => (
            <div key={`overdue-${item.number}`} className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-300 font-mono font-bold text-sm border border-amber-500/30" title={`Score: ${item.overdue_score}`}>
              {String(item.number).padStart(2, '0')}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

export const MetricsGrid = ({ prediction }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-grid">
    <Card className="glass-card">
      <CardContent className="p-4 text-center">
        <BarChart3 size={24} className="mx-auto text-purple-400 mb-2" />
        <p className="text-2xl font-bold text-white font-mono">{prediction.metrics.draws_analyzed}</p>
        <p className="text-slate-400 text-xs">Sorteos Analizados</p>
      </CardContent>
    </Card>
    <Card className="glass-card">
      <CardContent className="p-4 text-center">
        <Flame size={24} className="mx-auto text-pink-400 mb-2" />
        <p className="text-2xl font-bold text-white font-mono">{prediction.metrics.heat_index}</p>
        <p className="text-slate-400 text-xs">Indice de Calor</p>
      </CardContent>
    </Card>
    <Card className="glass-card">
      <CardContent className="p-4 text-center">
        <TrendingUp size={24} className="mx-auto text-green-400 mb-2" />
        <p className="text-2xl font-bold text-white font-mono">{prediction.metrics.overdue_score}</p>
        <p className="text-slate-400 text-xs">Score Overdue</p>
      </CardContent>
    </Card>
  </div>
);
